import { gql } from '@apollo/client'
import { SINGLE_PROJECT_MODEL } from './project'

const PAYMENT_MODEL = `
    method
    amount
    credits
    enteredBy
    description
    paymentId
    createdAt
`

export const JOB_MODEL = `
    id
    interviewer {
        id
    }
    user {
        id
        fullName
        firstName
        lastName
        email
        expert {
            id
            country
            stripeAccount {
                stripeId
                isConnected
            }
        }
    }
    project {
        id
        code
        team {
            id
            name
        }
    }
    meetingAt
    zoomMeeting {
        zoomId
    }
    completedAt
    invite_status
    response_status
    overall_status
    payment {
        ${PAYMENT_MODEL}
    }
    ratingOnExpert
    reviewOnExpert
    invitedAt
    invitedReminderAt
    createdAt
`

export const MANAGE_JOB_MODEL = `
    id
    interviewers {
        id
        email
        fullName
        firstName
        lastName
    }
    user {
        id
        fullName
        firstName
        lastName
        email
        timeZone
        expert {
            id
            status
            country
            title
            credits
            organization {
                name
            }
            stripeAccount {
                stripeId
                isConnected
            }
            reminder_notify
            payment_notify
            internal_tags
            hourlyRate
            functions
            medical_specialties
            technology_tags
        }
    }
    project {
        id
        title
        status
        description
        code
        type
        service_type
        qualtricsSurvey {
            surveyId
        }
        jobs {
            id
            user {
                id
            }
            email_logs {
                subject
                sent_at
            }
            invite_status
            response_status
        }
        owners {
            id
            email
        }
        team {
            id
            name
            perCreditPrice
        }
    }
    email_logs {
        subject
        sent_at
    }
    invite_status
    invitedAt
    invitedReminderAt
    response_status
    overall_status
    creditDeduction {
        ${PAYMENT_MODEL}
    }
    payment {
        ${PAYMENT_MODEL}
    }
    reviewOnExpert
    ratingOnExpert
    meetingAt
    completedAt
    ipAddress
    updatedAt
    offerRate
    qualtricsResponse {
        responseId
    }
    zoomMeeting {
        zoomId
        meetingReport {
            status
            participants {
                name
                email
                joinedAt
                leftAt
                numMinutes
            }
        }
    }
`

export const LOAD_MANAGED_JOB_QUERY = gql`
    query Job($id: String!) {
        job(id: $id) {
            ${MANAGE_JOB_MODEL}
        }
    }
`

export const LOAD_MANAGE_EXPERT_CARD = gql`
    query Job($id: String!) {
        job(id: $id) {
            ${MANAGE_JOB_MODEL}
        }
    }
`

export const JOB_BASIC = gql`
    query Job($id: String!) {
        job(id: $id) {
            id
            overall_status
            meetingAt
            offerRate
            adminNotes
            payment {
                ${PAYMENT_MODEL}
            }
            bonuses {
                ${PAYMENT_MODEL}
            }
            user {
                id
                email
                fullName
            }
            expert {
                id
                country
                stripeAccount {
                    stripeId
                    isConnected
                }
            }
            project {
                id
                type
                title
                team {
                    id
                    name
                }
                qualtricsSurvey {
                    surveyId
                }
            }
            zoomMeeting {
                meetingLink
                zoomUserId
                zoomId
            }
        }
    }
`

export const LOAD_JOBS_QUERY = gql`
    query Jobs($filters: JobFilters, $pagination: Pagination, $sortBy: JobSortBy) {
      jobs(filters: $filters, pagination: $pagination, sortBy: $sortBy) {
          ${JOB_MODEL}
      }
    }
`

export const LOAD_PAYMENT_JOBS_QUERY = gql`
    query Jobs(
        $filters: JobFilters
        $pagination: Pagination
        $sortBy: JobSortBy
    ) {
        jobs(filters: $filters, pagination: $pagination, sortBy: $sortBy) {
            id
            payment {
                ${PAYMENT_MODEL}
            }
            user {
                id
                fullName
                firstName
                lastName
                email
                expert {
                    id
                    country
                    stripeAccount {
                        stripeId
                        isConnected
                    }
                }
            }
            project {
                id
                code
                team {
                    id
                    name
                }
            }
        }
    }
`

export const LOAD_JOBS_TOTAL_COUNT = gql`
    query Jobs($filters: JobFilters, $pagination: Pagination) {
        jobs(filters: $filters, pagination: $pagination) {
            id
        }
    }
`

export const LOAD_USER_JOBS_QUERY = gql`
    query UserJobs($id: String!) {
        user(id: $id) {
            id
            jobs {
                id
                project {
                    id
                    title
                    type
                    status
                    description
                }
                completedAt
                invite_status
                response_status
                overall_status
                payment {
                    ${PAYMENT_MODEL}
                }
                ratingOnExpert
                reviewOnExpert
                createdAt
            }
        }
    }
`

export const JOB_ADMIN_NOTES = gql`
    query Job($id: String!) {
        job(id: $id) {
            id
            adminNotes
        }
    }
`

export const UPDATE_JOB_MUTATION = gql`
    mutation UpdateJob($userId: ID!, $projectId: ID!, $job: JobInput!) {
        updateJob(userId: $userId, projectId: $projectId, job: $job) {
            id
            overall_status
            project {
                ${SINGLE_PROJECT_MODEL}
            }
            offerRate
            ratingOnExpert
        }
    }
`

export const MARK_AS_PAID = gql`
    mutation MarkAsPaid($userId: ID!, $projectId: ID!, $job: JobInput!) {
        updateJob(userId: $userId, projectId: $projectId, job: $job) {
            id
            overall_status
        }
    }
`

export const COMPLETE_JOB_MUTATION = gql`
    mutation CompleteJob($jobId: ID!) {
        completeJob(jobId: $jobId) {
            id
            project {
                id
                jobs {
                    id
                    overall_status
                }
                team {
                    id
                }
            }
            overall_status
        }
    }
`

export const DELETE_JOB_MUTATION = gql`
    mutation DeleteJob($id: ID!) {
        deleteJob(id: $id) {
            id
        }
    }
`
